import { Icon } from "@iconify/react";
import Modal from "@mui/material/Modal";
import { styled } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import { renderCTAButton, renderGhostButton, renderOutlineButton, renderPreviewButton } from "../components/buttons";
import { renderInputWithAffixAndLabel, renderInputWithLabel } from "../components/form-components/inputs";
import { formatDate } from "../lib/utils";
import { Checkbox } from "@mui/material";

const CameraModalContent = styled("div")(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: "8px",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "relative",
  outline: "none",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",

  // Mobile and tablet (portrait)
  width: "90%",
  height: "90%",
  maxHeight: "90vh",
  overflowY: "auto",

  // Desktop and larger tablets
  [theme.breakpoints.up("md")]: {
    width: "800px",
    height: "600px",
    maxHeight: "80vh",
  },

  "& .camera-container": {
    width: "100%",
    height: "calc(100% - 100px)", // Reserve space for buttons
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#000",
    marginBottom: "16px",
    borderRadius: "4px",
    overflow: "hidden",
  },

  "& .camera-video": {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },

  "& .buttons-container": {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    gap: "16px",
    marginTop: "16px",
    width: "100%",
  },

  "& .error-message": {
    color: "red",
    marginTop: "8px",
    fontSize: "0.875rem",
    textAlign: "center",
  },
}));

export const collectionDocument = {
  document_name: "Plinth Stability Certificate",
  job_id: 1,
  job_name: "Project 1",
  job_code: "JOB17052025",
  job_date: "2025-07-31",
  site_address: "Lorem ipsum dolor sit amet, Consectetur adipiscing elit",
  locality: "Locality 1",
  document_reference_number: "DOCREF17052025",
  isSubmitted: false,
  name: "John Doe",
  contact: "1234567890",
  photo: "https://via.placeholder.com/150",
  additional_images: "https://via.placeholder.com/150",
  collectionDate: "2025-05-18",
  comment: "Comment Text: Lorem, ipsum dolor sit amet consectetur adipisicing elit. Totam, quibusdam?",
};

export default function DocumentCollection() {
  const [data, setData] = useState<Record<string, any>>({});
  const isSubmitted = useMemo(() => data.isSubmitted, [data]);
  const [action, setAction] = useState("");
  const initialFormData = {
    name: "",
    contact: "",
    photo: "",
    additional_images: "",
  };
  const [formData, setFormData] = useState(initialFormData);
  const [isValidJobCode, setIsValidJobCode] = useState(false);
  const [isValidDocumentReferenceNumber, setIsValidDocumentReferenceNumber] = useState(false);

  const isValidFormData = useMemo(() => {
    if (!isValidJobCode || !isValidDocumentReferenceNumber) return false;

    const invalidValues = ["", null, undefined, NaN];
    if (invalidValues.includes(formData.name)) return false;
    if (invalidValues.includes(formData.contact)) return false;
    if (!formData.photo) return false;

    return true;
  }, [isValidJobCode, isValidDocumentReferenceNumber, formData]);

  // Add responsive state to adjust UI based on viewport
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setData(collectionDocument || []);
  }, []);

  // Check device type on mount and window resize
  useEffect(() => {
    const checkDeviceType = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    checkDeviceType();

    // Add event listener for window resize
    window.addEventListener("resize", checkDeviceType);

    // Cleanup
    return () => {
      window.removeEventListener("resize", checkDeviceType);
    };
  }, []);

  const renderRow = (label, value) => (
    <div className="document-collection-row">
      <span className="label gray">{label}</span>
      <span className="value text_right">{value}</span>
    </div>
  );

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Camera capture state
  const [showCamera, setShowCamera] = useState(false);
  const [openedCamera, setOpenedCamera] = useState("");
  const [cameraErrorPhoto, setCameraErrorPhoto] = useState("");
  const [cameraErrorAdditional, setCameraErrorAdditional] = useState("");
  const [videoRef, setVideoRef] = useState(null);
  const [stream, setStream] = useState(null);

  useEffect(() => {
    if (videoRef && stream) {
      videoRef.srcObject = stream;
      videoRef.play().catch((err) => {
        console.error("Error playing video:", err);
        if (openedCamera === "photo") setCameraErrorPhoto("Failed to start video stream.");
        else if (openedCamera === "additional") setCameraErrorAdditional("Failed to start video stream.");
      });
    }
  }, [videoRef, stream, openedCamera]);

  // Open camera
  const handleOpenCamera = async () => {
    setOpenedCamera("photo");
    setCameraErrorPhoto("");
    try {
      // Use different constraints based on device type
      const constraints = {
        video: {
          facingMode: "environment", // Prefer back camera if available
          width: { ideal: window.innerWidth > 800 ? 1280 : 720 },
          height: { ideal: window.innerWidth > 800 ? 720 : 1280 },
        },
      };

      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

      setStream(mediaStream);
      setShowCamera(true);
    } catch (err) {
      console.error("Camera access error:", err);
      setCameraErrorPhoto("Unable to access camera. Please ensure camera permissions are enabled.");
    }
  };

  // Capture photo
  const handleCapturePhoto = () => {
    if (!videoRef) return;

    try {
      const canvas = document.createElement("canvas");
      canvas.width = videoRef.videoWidth;
      canvas.height = videoRef.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoRef, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        if (blob) {
          // Create a File object from the blob with a meaningful filename
          const photoFile = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
          handleInputChange("photo", photoFile);
          setShowCamera(false);
          if (stream) {
            stream.getTracks().forEach((track) => track.stop());
          }
        }
      }, "image/jpeg");
    } catch (error) {
      console.error("Error capturing photo:", error);
      if (openedCamera === "photo") setCameraErrorPhoto("Failed to capture photo. Please try again.");
      else if (openedCamera === "additional") setCameraErrorAdditional("Failed to capture photo. Please try again.");
    }
  };

  // Close camera
  const handleCloseCamera = () => {
    setShowCamera(false);
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  };

  // Handle additional images through camera only - now for a single image
  const [additionalCameraActive, setAdditionalCameraActive] = useState(false);

  const handleOpenAdditionalCamera = async () => {
    setOpenedCamera("additional");
    setCameraErrorAdditional("");
    try {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment", // Prefer back camera
        },
      });
      setStream(mediaStream);
      setAdditionalCameraActive(true);
      if (videoRef) {
        videoRef.srcObject = mediaStream;
        videoRef.play();
      }
    } catch (err) {
      console.error("Camera access error:", err);
      setCameraErrorAdditional("Unable to access camera. Please ensure camera permissions are enabled.");
    }
  };

  const handleCaptureAdditionalPhoto = () => {
    if (!videoRef) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.videoWidth;
    canvas.height = videoRef.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoRef, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (blob) {
        // Create a File object from the blob with a meaningful filename
        const photoFile = new File([blob], `additional_${Date.now()}.jpg`, { type: "image/jpeg" });
        handleInputChange("additional_images", photoFile);
        setAdditionalCameraActive(false);
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
      }
    }, "image/jpeg");
  };

  const handleCloseAdditionalCamera = () => {
    setAdditionalCameraActive(false);
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  };

  const removeAdditionalImage = () => {
    // Clear the additional image
    handleInputChange("additional_images", "");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isValidFormData) {
      setAction("approve");
      setData({
        ...data,
        name: formData.name,
        photo: formData.photo,
        additional_images: formData.additional_images,
        comment: formData.comment,
        isSubmitted: true,
        collectionDate: new Date().toISOString().split("T")[0],
      });
      // Here you would normally submit the form data to your backend
      console.log("Form submitted with data:", formData);
    }
  };

  return (
    <div className="document-collection-page">
      <header className="document-collection-page-header">
        <div className="logo_wrapper">
          <img src="/assets/images/Structualtants.svg" alt="Logo" className="logo small" />
          <div className="logo_text text_center">
            <span className="title ">Structualtants</span>
            <span className="subtile">Designs LLP</span>
            <span className="tagline">We make it simple.</span>
          </div>
        </div>
        <div className="">{collectionDocument.document_name}</div>
      </header>
      <main>
        <div className="column small">
          <label htmlFor="confirm_job_id" className="row">
            Job no: {collectionDocument.job_code}
            <Checkbox
              id="confirm_job_id"
              onChange={(e) => setIsValidJobCode(e.target.checked)}
              checked={isSubmitted || isValidJobCode}
              disabled={isSubmitted}
            />
            {/* <input
              type="checkbox"
              id="confirm_job_id"
              onChange={(e) => setIsValidJobCode(e.target.checked)}
              checked={isSubmitted || isValidJobCode}
              disabled={isSubmitted}
            /> */}
          </label>
          <label htmlFor="confirm_doc_ref" className="row">
            Document ref no: {collectionDocument.document_reference_number}
            <Checkbox
              id="confirm_doc_ref"
              onChange={(e) => setIsValidDocumentReferenceNumber(e.target.checked)}
              checked={isSubmitted || isValidDocumentReferenceNumber}
              disabled={isSubmitted}
            />
            {/* <input
              type="checkbox"
              id="confirm_doc_ref"
              onChange={(e) => setIsValidDocumentReferenceNumber(e.target.checked)}
              checked={isSubmitted || isValidDocumentReferenceNumber}
              disabled={isSubmitted}
            /> */}
          </label>
        </div>
        <hr />
        <div className="row">
          <span className="small">{collectionDocument.document_name}</span>
          {renderGhostButton("Download", false, "button", {})}
        </div>

        {!isSubmitted && (
          <div className="column ">
            <div className="font-md">Project Details</div>
            <div className="card rounded column small">
              <span>Basic Details</span>
              <div className="document-collection-job-details">
                <div className="column shrink">
                  {renderRow("Project Name", "Project 1")}
                  {renderRow("Project Date", formatDate(new Date()))}
                </div>
                <div className="column shrink">
                  {renderRow("Site Address", "804 F, Random street, random lane, Wakad, Pune, Maharashtra 400060")}
                  {renderRow("Locality", "Wakad, Pune")}
                </div>
              </div>
            </div>
          </div>
        )}
        {isSubmitted ? (
          <div className="column small">
            <div className="row">
              <span>Name</span>
              <span className="text_right">{data.name}</span>
            </div>
            <div className="row">
              <span>Contact Number</span>
              <span className="text_right">{data.contact}</span>
            </div>
            <div className="column shrink">
              <span>Photo of person collecting document</span>
              <div className="row">
                <span className="gray small">Uploaded on {formatDate(data.collectionDate)}</span>
                {renderPreviewButton("view", "link", { to: data.photo, target: "_blank" })}
              </div>
            </div>
            <div className="column shrink">
              <span>Additional Image</span>
              <div className="row">
                <span className="gray small">Uploaded on {formatDate(data.collectionDate)}</span>
                {renderPreviewButton("view", "link", { to: data.additional_images, target: "_blank" })}
              </div>
            </div>
            <div className="column shrink">
              <span>Comment</span>
              <span className="">{data.comment}</span>
            </div>
          </div>
        ) : (
          <form id="doc-collection-form" className="column small" onSubmit={handleSubmit}>
            {renderInputWithLabel("Name", "name", "name", "text", formData.name, "column", {
              onChange: (e) => handleInputChange("name", e.target.value),
              required: true,
              placeholder: "Enter name",
            })}
            {renderInputWithAffixAndLabel(
              "Contact Number",
              "contact",
              "contact",
              "text",
              formData.contact,
              "+91",
              null,
              "column",
              null,
              {
                onChange: (e) => handleInputChange("contact", e.target.value),
                minLength: 10,
                maxLength: 10,
                required: true,
                placeholder: "Enter contact number",
              }
            )}
            <div className="column shrink align_start">
              <span>Photo of person collecting document</span>
              {formData.photo && (
                <div className="column align_start image_container">
                  <img src={(URL.createObjectURL as any)(formData.photo)} alt="Captured" />
                </div>
              )}
              {renderOutlineButton(formData.photo ? "Retake photo" : "Open camera", true, "button", {
                onClick: () => handleOpenCamera(),
              })}
              {cameraErrorPhoto && <span className="error-message">{cameraErrorPhoto}</span>}
              {showCamera && (
                <ModalWrapper open={showCamera} onClose={handleCloseCamera}>
                  <CameraModalContent>
                    <div className="camera-container">
                      <video ref={setVideoRef} className="camera-video" autoPlay playsInline />
                    </div>
                    <div className="buttons-container">
                      {renderCTAButton("Capture", "button", { onClick: handleCapturePhoto })}
                      {renderGhostButton("Cancel", false, "button", { onClick: handleCloseCamera })}
                    </div>
                    {cameraErrorPhoto && <span className="error-message">{cameraErrorPhoto}</span>}
                  </CameraModalContent>
                </ModalWrapper>
              )}
            </div>
            <div className="column shrink align_start">
              <span className="label">Additional image</span>
              {formData.additional_images && (
                <div className="column align_start image_container">
                  <img src={(URL.createObjectURL as any)(formData.additional_images)} alt="Additional" />
                  <button type="button" onClick={removeAdditionalImage}>
                    <Icon icon="mingcute:close-line" className="icon" />
                  </button>
                </div>
              )}
              <div className="row" style={{ gap: "8px" }}>
                {renderOutlineButton("Take Photo", true, "button", {
                  onClick: handleOpenAdditionalCamera,
                  disabled: formData.additional_images,
                })}

                <label
                  htmlFor="gallery-upload"
                  className={`label cta_outline_btn btn ${formData.additional_images ? "disabled" : ""}`}
                >
                  From Gallery
                  <input
                    type="file"
                    id="gallery-upload"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        handleInputChange("additional_images", e.target.files[0]);
                        e.target.value = ""; // Reset input
                      }
                    }}
                  />
                </label>
              </div>
              {cameraErrorAdditional && <span className="error-message">{cameraErrorAdditional}</span>}
              {additionalCameraActive && (
                <ModalWrapper open={additionalCameraActive} onClose={handleCloseAdditionalCamera}>
                  <CameraModalContent>
                    <div className="camera-container">
                      <video ref={setVideoRef} className="camera-video" autoPlay playsInline />
                    </div>
                    <div className="buttons-container">
                      {renderCTAButton("Capture", "button", { onClick: handleCaptureAdditionalPhoto })}
                      {renderGhostButton("Cancel", false, "button", { onClick: handleCloseAdditionalCamera })}
                    </div>
                    {cameraErrorAdditional && <span className="error-message">{cameraErrorAdditional}</span>}
                  </CameraModalContent>
                </ModalWrapper>
              )}
            </div>
            {renderInputWithLabel("Add comment", "comment", "comment", "text", formData.comment, "column", {
              onChange: (e) => handleInputChange("comment", e.target.value),
            })}
            <div className="row end">
              {renderCTAButton("Submit", "submit", {
                form: "doc-collection-form",
                disabled: !isValidFormData,
              })}
            </div>
          </form>
        )}
      </main>
      <SuccessModal open={action === "approve"} onClose={() => setAction("")} />
    </div>
  );
}

const ModalWrapper = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const SuccessModal = ({ open, onClose }) => {
  return (
    <ModalWrapper open={open} onClose={onClose}>
      <div
        className="modal_container column align_center"
        style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          padding: "24px",
          maxWidth: "90%",
          width: "400px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          outline: "none",
        }}
      >
        <Icon icon="duo-icons:approved" className="primary_icon" />
        <span className="font-md">Document collected successfully.</span>
        {renderCTAButton("Close", "button", { onClick: onClose })}
      </div>
    </ModalWrapper>
  );
};
